<template>
  <div class="content final-testing-page">
    <div class="content-header">
      <button class="content-header__exit" @click="logout">Выйти</button>
      <RouterLink :to="{name: 'general-testing'}" class="content-header__close"><i class="fas fa-times"></i></RouterLink>
    </div>
    <div class="content-header__title">Итоговое тестирование</div>
    <div class="content-main">
      <div class="content-main_desc">
        <div class="final-testing-hero">
          <div>
            <h1 class="final-testing-hero__title">Выберите блоки для итогового тестирования</h1>
            <p class="final-testing-hero__text">Нужно выбрать минимум два блока.</p>
          </div>
          <div class="final-testing-hero__stats">
            <div><span>Выбрано блоков</span><strong>{{ selectedCount }}</strong></div>
            <div><span>Всего вопросов</span><strong>{{ selectedQuestionsCount }}</strong></div>
          </div>
        </div>

        <div v-if="isLoading" class="final-testing-state">Загрузка блоков...</div>
        <div v-else-if="blocks.length === 0" class="final-testing-state">Блоки общего тестирования пока не найдены.</div>
        <template v-else>
          <div v-if="errorMessage" class="final-testing-error">{{ errorMessage }}</div>

          <div class="final-testing-list">
            <button
              v-for="block in blocks"
              :key="block.id"
              type="button"
              class="final-testing-card"
              :class="{ 'is-selected': isSelected(block.id) }"
              @click="toggleBlock(block.id)"
            >
              <div class="final-testing-card__top">
                <div class="final-testing-card__check"><i class="fas" :class="isSelected(block.id) ? 'fa-check' : 'fa-plus'"></i></div>
                <div class="final-testing-card__name">{{ block.title }}</div>
              </div>
              <div class="final-testing-card__stats">
                <div>
                  <span>Тестов</span>
                  <strong>{{ block.testsCount }}</strong>
                </div>
                <div>
                  <span>Вопросов</span>
                  <strong>{{ block.questionsCount }}</strong>
                </div>
                <div>
                  <span>Пройдено</span>
                  <strong>{{ block.completedQuestionsCount }}</strong>
                </div>
              </div>
            </button>
          </div>

          <div v-if="showStatusNotice" class="final-testing-status" :class="statusToneClass">
            <div class="final-testing-status__title">{{ statusTitle }}</div>
            <div class="final-testing-status__text">{{ statusText }}</div>
            <RouterLink
              v-if="status?.attempt_id"
              :to="{ name: 'final-testing-attempt', params: { attemptId: status.attempt_id } }"
              class="final-testing-status__link"
            >
              {{ statusLinkLabel }}
            </RouterLink>
          </div>

          <div class="final-testing-footer">
            <div class="final-testing-footer__summary">
              <div class="final-testing-footer__label">Выбранные блоки:</div>
              <div class="final-testing-footer__chips">
                <span v-for="block in selectedBlocks" :key="block.id" class="final-testing-chip">{{ block.title }}</span>
                <span v-if="selectedBlocks.length === 0" class="final-testing-footer__empty">Пока ничего не выбрано</span>
              </div>
            </div>
            <button class="button primary final-testing-footer__button" :disabled="!canStartAction || isStarting || isCheckingStatus" @click="startAttempt">
              {{ buttonLabel }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from "@vue/runtime-core";
import { useRouter } from "vue-router";
import { getFinalTestingStatus, getGeneralTesting, startFinalTesting } from "@/data/requests";

const getCount = (block, keys) => {
  for (const key of keys) {
    if (typeof block?.[key] === "number") return block[key];
  }

  for (const key of keys) {
    if (Array.isArray(block?.[key])) return block[key].length;
  }

  return 0;
};

const normalizeBlock = (block, index) => ({
  id: block?.id ?? index,
  title: block?.title ?? block?.name ?? block?.label ?? `Блок ${index + 1}`,
  testsCount: getCount(block, ["tests_count", "testsCount", "tests"]),
  questionsCount: getCount(block, ["questions_count", "questionsCount", "total_questions_count", "questions"]),
  completedQuestionsCount: getCount(block, ["passed_questions_count", "passedQuestionsCount", "completed_questions_count", "completedQuestionsCount"]),
});

export default {
  setup() {
    const logout = inject("logout");
    const token = inject("token");
    const router = useRouter();
    const blocks = ref([]);
    const selectedIds = ref([]);
    const isLoading = ref(true);
    const isStarting = ref(false);
    const isCheckingStatus = ref(false);
    const errorMessage = ref("");
    const status = ref(null);
    let statusRequestId = 0;

    const selectedBlocks = computed(() => blocks.value.filter((block) => selectedIds.value.includes(block.id)));
    const selectedCount = computed(() => selectedIds.value.length);
    const selectedQuestionsCount = computed(() => selectedBlocks.value.reduce((sum, block) => sum + block.questionsCount, 0));
    const canCheckStatus = computed(() => selectedIds.value.length >= 2);
    const initialStatusIds = computed(() => blocks.value.slice(0, 2).map((block) => block.id));
    const isBlocked = computed(() => !!status.value?.is_blocked);
    const hasInProgressAttempt = computed(() => status.value?.has_attempt && !status.value?.is_completed && status.value?.status === "in_progress");
    const canStartAction = computed(() => canCheckStatus.value && !isBlocked.value && !hasInProgressAttempt.value);
    const showStatusNotice = computed(() => !!status.value?.has_attempt || isBlocked.value || hasInProgressAttempt.value);

    const statusToneClass = computed(() => {
      if (isBlocked.value) return "is-danger";
      if (hasInProgressAttempt.value) return "is-info";
      if (status.value?.status === "passed") return "is-success";
      return "is-warning";
    });

    const statusTitle = computed(() => {
      if (isBlocked.value) return "Сейчас пройти итоговое тестирование нельзя";
      if (hasInProgressAttempt.value) return "У вас уже есть незавершенная попытка";
      if (status.value?.status === "passed") return "Предыдущая попытка пройдена";
      return "Предыдущая попытка не пройдена";
    });

    const statusText = computed(() => {
      if (isBlocked.value) return `Новая попытка будет доступна после ${status.value.blocked_until}.`;
      if (hasInProgressAttempt.value) return "Можно вернуться в уже созданную попытку и продолжить с того же места.";
      if (status.value?.status === "passed") return "Предыдущая попытка уже завершена успешно. После окончания периода блокировки можно будет начать новую.";
      return "Предыдущая попытка завершена. После окончания периода блокировки можно будет начать новую.";
    });

    const statusLinkLabel = computed(() => hasInProgressAttempt.value ? "Продолжить попытку" : "Открыть попытку");

    const buttonLabel = computed(() => {
      if (isCheckingStatus.value) return "Проверяем доступность...";
      if (isStarting.value) return "Переходим...";
      if (hasInProgressAttempt.value) return "Продолжить итоговое тестирование";
      if (isBlocked.value) return "Итоговое тестирование временно недоступно";
      if (!canCheckStatus.value) return "Выберите минимум 2 блока";
      return "Начать итоговое тестирование";
    });

    function isSelected(blockId) {
      return selectedIds.value.includes(blockId);
    }

    function toggleBlock(blockId) {
      errorMessage.value = "";
      if (isSelected(blockId)) {
        selectedIds.value = selectedIds.value.filter((id) => id !== blockId);
        return;
      }

      selectedIds.value = [...selectedIds.value, blockId];
    }

    async function requestStatus(blockIds) {
      if (!Array.isArray(blockIds) || blockIds.length < 2) return;
      const requestId = ++statusRequestId;
      isCheckingStatus.value = true;

      try {
        const sortedIds = [...blockIds].sort((a, b) => Number(a) - Number(b));
        const response = await getFinalTestingStatus(token.value, sortedIds);
        if (requestId !== statusRequestId) return;
        status.value = response;
      } finally {
        if (requestId === statusRequestId) {
          isCheckingStatus.value = false;
        }
      }
    }

    async function checkStatus() {
      if (!canCheckStatus.value) return;
      await requestStatus(selectedIds.value);
    }

    async function loadInitialStatus() {
      if (initialStatusIds.value.length < 2) {
        status.value = null;
        return;
      }
      await requestStatus(initialStatusIds.value);
    }

    async function loadBlocks() {
      isLoading.value = true;
      const response = await getGeneralTesting(token.value);
      const rawBlocks = Array.isArray(response) ? response : response?.blocks ?? response?.data ?? [];
      blocks.value = rawBlocks.map(normalizeBlock);
      isLoading.value = false;
      await loadInitialStatus();
    }

    async function startAttempt() {
      if (!canStartAction.value || isStarting.value) return;

      if (hasInProgressAttempt.value && status.value?.attempt_id) {
        await router.push({ name: "final-testing-attempt", params: { attemptId: status.value.attempt_id } });
        return;
      }

      isStarting.value = true;
      errorMessage.value = "";
      const response = await startFinalTesting(token.value, selectedIds.value);
      isStarting.value = false;

      if (response.code === 422) {
        errorMessage.value = response.data?.message ?? "Не удалось запустить итоговое тестирование";
        return;
      }

      const attemptId = response.data?.id;
      if (!attemptId) {
        errorMessage.value = "Не удалось открыть итоговое тестирование";
        return;
      }

      await router.push({ name: "final-testing-attempt", params: { attemptId } });
    }

    watch(selectedIds, checkStatus, { deep: true });
    onMounted(loadBlocks);

    return {
      logout, blocks, selectedBlocks, selectedCount, selectedQuestionsCount,
      isLoading, isStarting, isCheckingStatus, errorMessage, status,
      showStatusNotice, statusToneClass, statusTitle, statusText, statusLinkLabel,
      canStartAction, buttonLabel, isSelected, toggleBlock, startAttempt,
    };
  },
};
</script>

<style scoped lang="scss">
.content-main_desc { max-width: 1180px; width: 100%; }
.final-testing-hero {
  margin-top: 20px;
  padding: 32px;
  border-radius: 30px;
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(260px, .8fr);
  gap: 24px;
  color: #fff;
  background: radial-gradient(circle at top right, rgba(255,255,255,.16), transparent 24%), linear-gradient(135deg, #183153 0%, #00588d 42%, #13a37f 100%);
}
.final-testing-hero__title { margin: 0 0 12px; font-size: 40px; line-height: 1.05; }
.final-testing-hero__text { margin: 0; max-width: 640px; line-height: 1.7; color: rgba(255,255,255,.88); }
.final-testing-hero__stats { display: grid; gap: 12px; }
.final-testing-hero__stats div { padding: 18px 20px; border-radius: 20px; background: rgba(255,255,255,.14); }
.final-testing-hero__stats span { display: block; font-size: 12px; opacity: .8; margin-bottom: 8px; }
.final-testing-hero__stats strong { font-size: 30px; line-height: 1; }
.final-testing-state { margin-top: 24px; font-size: 16px; }
.final-testing-error { margin-top: 22px; padding: 16px 18px; border-radius: 18px; background: #fff2f4; border: 1px solid #ffd1d9; color: #b42343; }
.final-testing-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin-top: 22px; }
.final-testing-card { border: 1px solid #dfe9f1; border-radius: 24px; padding: 22px; background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%); box-shadow: 0 18px 36px rgba(13, 61, 97, .06); text-align: left; cursor: pointer; transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease; }
.final-testing-card:hover { transform: translateY(-2px); }
.final-testing-card.is-selected { border-color: #0a7abf; box-shadow: 0 0 0 2px rgba(10, 122, 191, .12), 0 18px 36px rgba(10, 122, 191, .12); background: linear-gradient(180deg, #f4fbff 0%, #eaf7ff 100%); }
.final-testing-card__top { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 18px; }
.final-testing-card__check { width: 36px; height: 36px; border-radius: 12px; display: grid; place-items: center; background: #eef5fb; color: #0a6ca8; flex: 0 0 auto; }
.final-testing-card.is-selected .final-testing-card__check { background: #0a7abf; color: #fff; }
.final-testing-card__name { font-size: 24px; line-height: 1.15; color: #17324a; }
.final-testing-card__stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.final-testing-card__stats div { padding: 14px; border-radius: 16px; background: rgba(10, 108, 168, .06); }
.final-testing-card__stats span { display: block; font-size: 12px; color: #648197; margin-bottom: 6px; }
.final-testing-card__stats strong { font-size: 24px; color: #0a6ca8; }
.final-testing-status { margin-top: 22px; padding: 18px 20px; border-radius: 20px; border: 1px solid #dde8f1; background: #fff; }
.final-testing-status.is-info { background: #eef7ff; border-color: #cfe7f8; }
.final-testing-status.is-success { background: #eefaf5; border-color: #cfeada; }
.final-testing-status.is-warning { background: #fff8ef; border-color: #f4dfbc; }
.final-testing-status.is-danger { background: #fff2f1; border-color: #f4c6c2; }
.final-testing-status__title { font-size: 18px; color: #17324a; margin-bottom: 8px; }
.final-testing-status__text { color: #5a7487; line-height: 1.6; }
.final-testing-status__link { display: inline-block; margin-top: 12px; color: #0a6ca8; font-weight: 600; text-decoration: none; }
.final-testing-footer { margin-top: 22px; padding: 22px; border-radius: 24px; background: #fff; border: 1px solid #dde8f1; display: flex; justify-content: space-between; gap: 18px; align-items: center; }
.final-testing-footer__summary { flex: 1 1 auto; }
.final-testing-footer__label { color: #5d788d; margin-bottom: 10px; }
.final-testing-footer__chips { display: flex; flex-wrap: wrap; gap: 10px; }
.final-testing-chip { padding: 10px 14px; border-radius: 999px; background: #eef7ff; color: #0a6ca8; font-weight: 600; }
.final-testing-footer__empty { color: #8ba0b2; }
.final-testing-footer__button { min-width: 300px; }
@media all and (max-width: 980px) { .final-testing-hero, .final-testing-footer, .final-testing-list { grid-template-columns: 1fr; } .final-testing-footer { flex-direction: column; align-items: stretch; } .final-testing-footer__button { min-width: 0; } }
@media all and (max-width: 680px) { .final-testing-hero { padding: 24px; } .final-testing-hero__title { font-size: 30px; } .final-testing-card__stats { grid-template-columns: 1fr; } }
</style>


